import CryptoJS from "crypto-js";
import { ouchnCustom } from "@/service";
import { baseURL } from "./config";

const BOOK_AI_AK = "sd2zd6E8vmNHxtkbTM6fqNMG";
const BOOK_AI_SK = "e27Gd9IRKigaNTQ6ra9Fy0ebPC5TWt";
const BOOK_AI_BOOK_ID = "019eb632-b782-72d7-a368-649dd393b61f";
const BOOK_AI_PATH = "/BookAIQuestionAnswering/ChatStream";

function urlEncodeBody(value) {
  if (!value) return "";
  let encoded = encodeURIComponent(value).replace(/%20/g, "+");
  encoded = encoded.replace(/%[0-9a-fA-F]{2}/g, (m) => m.toUpperCase());
  encoded = encoded
    .replace(/\+/g, "%2B")
    .replace(/\*/g, "%2A")
    .replace(/!/g, "%21")
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/~/g, "%7E");
  return encoded;
}

function sha256Hex(data) {
  return CryptoJS.SHA256(data).toString(CryptoJS.enc.Hex).toLowerCase();
}

function hmacSha256Hex(key, message) {
  return CryptoJS.HmacSHA256(message, key).toString(CryptoJS.enc.Hex).toLowerCase();
}

function generateSignature(ak, sk, timestamp, nonce, method, path, body) {
  const encodedBody = urlEncodeBody(body);
  const bodyHash = sha256Hex(encodedBody);
  const signString = `${method}\n${path}\n${ak}\n${timestamp}\n${nonce}\n\n${bodyHash}`;
  const sign = sha256Hex(signString);
  return hmacSha256Hex(sk, sign);
}

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function normalizeRobotAgentValue(value) {
  return String(value || "")
    .replace(/^['"]|['"]$/g, "")
    .trim();
}

/** 优先取路由 query/params 中的 robotAgent，否则回退到 VUE_APP_ROBOT_AGENT */
export function resolveRobotAgent(route) {
  if (route) {
    const routeAgent =
      (route.query && route.query.robotAgent) ||
      (route.params && route.params.robotAgent);
    if (routeAgent != null && String(routeAgent).trim() !== "") {
      return normalizeRobotAgentValue(routeAgent);
    }
  }
  return normalizeRobotAgentValue(process.env.VUE_APP_ROBOT_AGENT);
}

export function isBookAiAgent(route) {
  return resolveRobotAgent(route) === "bookAi";
}

export function createBookAiSessionId() {
  return uuid();
}

function buildUpstreamPayload(question, sessionId) {
  return {
    inputMsg: {
      msgId: uuid(),
      content: question,
      senderType: "user",
    },
    sessionId: sessionId,
    bookId: BOOK_AI_BOOK_ID,
  };
}

/**
 * 构建中转 API 请求体，与后端 BookAiChatProxyRequest 对齐：
 * { bookAiAuth: { ak, timestamp, nonce, signature, body } }
 * 其中 body 为上游接口参与签名的原始 JSON 字符串
 */
function buildProxyRequest(question, sessionId) {
  const upstreamPayload = buildUpstreamPayload(question, sessionId);
  const upstreamBody = JSON.stringify(upstreamPayload);
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = uuid().replace(/-/g, "");
  const signature = generateSignature(
    BOOK_AI_AK,
    BOOK_AI_SK,
    timestamp,
    nonce,
    "POST",
    BOOK_AI_PATH,
    upstreamBody
  );
  return {
    proxyBody: JSON.stringify({
      bookAiAuth: {
        ak: BOOK_AI_AK,
        timestamp,
        nonce,
        signature,
        body: upstreamBody,
      },
    }),
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "X-AK": BOOK_AI_AK,
      "X-Timestamp": timestamp,
      "X-Nonce": nonce,
      "X-Signature": signature,
    },
    sessionId,
  };
}

function getBookAiChatUrl() {
  return `http://124.133.23.84:10000/ouchn-custom-gaojinhao/web/bookAi/chatStream`;
}

function consumeSseLine(line, state) {
  if (!line.startsWith("data:")) return;
  const data = line.substring(5).trim();
  if (!data || data === "[DONE]") {
    state.loadingCompleted = true;
    return;
  }
  try {
    const json = JSON.parse(data);
    const chunkContent = json.content || json.Content || "";
    const msgType = String(json.msgType || json.MsgType || "").toLowerCase();
    if (chunkContent) {
      if (msgType === "chunk") {
        state.content += chunkContent;
      } else if (msgType === "aimsg") {
        state.content = chunkContent;
      } else {
        state.content += chunkContent;
      }
    }
    if (json.sessionId || json.SessionId) {
      state.sessionId = json.sessionId || json.SessionId;
    }
    // 中间轮次的空 aiMsg 仅表示段落结束，非整段回复完成
    if (msgType === "aimsg" && chunkContent) {
      state.loadingCompleted = true;
    }
  } catch (e) {
    // ignore invalid json chunk
  }
}

/**
 * 调用图书智能体流式问答
 */
export async function streamBookAiChat({ question, sessionId, signal, onUpdate }) {
  const requestSessionId = sessionId || createBookAiSessionId();
  const { proxyBody, headers } = buildProxyRequest(question, requestSessionId);
  const requestUrl = getBookAiChatUrl();
  if (process.env.NODE_ENV === "development") {
    console.info("[bookAi] POST", requestUrl, JSON.parse(proxyBody));
  }
  const response = await fetch(requestUrl, {
    method: "POST",
    headers: {
      ...headers,
      platform: "custom",
      Authorization: "Bearer " + localStorage.getItem("token"),
    },
    body: proxyBody,
    signal,
  });
  if (!response.ok) {
    let errorMsg = `bookAi request failed: ${response.status}`;
    try {
      const errorText = await response.text();
      if (errorText) {
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.msg || errorJson.message || errorText;
        } catch (e) {
          errorMsg = errorText;
        }
      }
    } catch (e) {
      // ignore parse error
    }
    throw new Error(errorMsg);
  }
  if (!response.body) {
    throw new Error("bookAi response body is empty");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let lineBuffer = "";
  const state = {
    content: "",
    loadingCompleted: false,
    sessionId: requestSessionId,
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split(/\r?\n/);
    lineBuffer = lines.pop() || "";
    lines.forEach((line) => consumeSseLine(line, state));
    if (onUpdate) {
      onUpdate({
        content: state.content,
        loadingCompleted: state.loadingCompleted,
        sessionId: state.sessionId,
      });
    }
  }

  if (lineBuffer.trim()) {
    consumeSseLine(lineBuffer, state);
  }
  state.loadingCompleted = true;
  if (onUpdate) {
    onUpdate({
      content: state.content,
      loadingCompleted: true,
      sessionId: state.sessionId,
    });
  }
  return {
    content: state.content,
    loadingCompleted: true,
    sessionId: state.sessionId,
  };
}
